/**
 * Sanskrit Varṇamālā Child Mnemonics & Stroke Order Guide
 * Maps every letter to a friendly animal/object mnemonic with Sanskrit word,
 * English/Hindi translations, emoji, and stroke-by-stroke handwriting rules.
 */

export interface LetterMnemonic {
  letter: string;
  iast: string;
  wordSan: string;
  wordIast: string;
  wordEn: string;
  wordHi: string;
  emoji: string;
  strokeOrder: string[];
  group: 'svara' | 'sparsha' | 'antastha' | 'ushmana' | 'ayogavaha' | 'samyukta';
  groupLabelSan: string;
  groupLabelEn: string;
  hoverNote?: string;
}

export const VARNAMALA_MNEMONICS: Record<string, LetterMnemonic> = {
  // ==========================================
  // SWARAS (Vowels / स्वराः)
  // ==========================================
  अ: {
    letter: 'अ',
    iast: 'a',
    wordSan: 'अश्वः',
    wordIast: 'aśvaḥ',
    wordEn: 'Horse',
    wordHi: 'घोड़ा',
    emoji: '🐴',
    strokeOrder: [
      '1. Draw top left curve (C-shape)',
      '2. Draw bottom left curve',
      '3. Draw horizontal connector from middle',
      '4. Draw vertical standing stem down',
      '5. Draw top horizontal bar (शिरोरेखा) last',
    ],
    group: 'svara',
    groupLabelSan: 'स्वराः (ह्रस्व)',
    groupLabelEn: 'Short Vowel',
  },
  आ: {
    letter: 'आ',
    iast: 'ā',
    wordSan: 'आम्रम्',
    wordIast: 'āmram',
    wordEn: 'Mango',
    wordHi: 'आम',
    emoji: '🥭',
    strokeOrder: [
      '1. Draw letter body "अ"',
      '2. Add second vertical standing stem (आ-मात्रा)',
      '3. Draw top horizontal bar across both stems',
    ],
    group: 'svara',
    groupLabelSan: 'स्वराः (दीर्घ)',
    groupLabelEn: 'Long Vowel',
  },
  इ: {
    letter: 'इ',
    iast: 'i',
    wordSan: 'इक्षुः',
    wordIast: 'ikṣuḥ',
    wordEn: 'Sugarcane',
    wordHi: 'गन्ना',
    emoji: '🎋',
    strokeOrder: [
      '1. Tiny vertical start peg',
      '2. Top S-loop turning right then left',
      '3. Bottom tail looping down diagonally',
      '4. Draw top horizontal bar (शिरोरेखा)',
    ],
    group: 'svara',
    groupLabelSan: 'स्वराः (ह्रस्व)',
    groupLabelEn: 'Short Vowel',
  },
  ई: {
    letter: 'ई',
    iast: 'ī',
    wordSan: 'ईशः',
    wordIast: 'īśaḥ',
    wordEn: 'Lord / Divine',
    wordHi: 'ईश्वर / प्रभु',
    emoji: '🕉️',
    strokeOrder: [
      '1. Draw base letter "इ"',
      '2. Draw top roof bar',
      '3. Add crescent curve antenna curling right on top',
    ],
    group: 'svara',
    groupLabelSan: 'स्वराः (दीर्घ)',
    groupLabelEn: 'Long Vowel',
  },
  उ: {
    letter: 'उ',
    iast: 'u',
    wordSan: 'उष्ट्रः',
    wordIast: 'uṣṭraḥ',
    wordEn: 'Camel',
    wordHi: 'ऊँट',
    emoji: '🐪',
    strokeOrder: [
      '1. Draw upper rounded curve to middle',
      '2. Draw lower sweeping curve',
      '3. Cap with top horizontal bar',
    ],
    group: 'svara',
    groupLabelSan: 'स्वराः (ह्रस्व)',
    groupLabelEn: 'Short Vowel',
  },
  ऊ: {
    letter: 'ऊ',
    iast: 'ū',
    wordSan: 'ऊर्णा',
    wordIast: 'ūrṇā',
    wordEn: 'Wool',
    wordHi: 'ऊन',
    emoji: '🧶',
    strokeOrder: [
      '1. Draw base letter "उ"',
      '2. Attach curved downward tail from middle back',
      '3. Cap with top horizontal bar',
    ],
    group: 'svara',
    groupLabelSan: 'स्वराः (दीर्घ)',
    groupLabelEn: 'Long Vowel',
  },
  ऋ: {
    letter: 'ऋ',
    iast: 'ṛ',
    wordSan: 'ऋषिः',
    wordIast: 'ṛṣiḥ',
    wordEn: 'Sage / Seer',
    wordHi: 'ऋषि',
    emoji: '🧘',
    strokeOrder: [
      '1. Vertical backbone stem',
      '2. Left upper and lower diagonal branches',
      '3. Right loop twisting down into small hook',
      '4. Cap with top horizontal bar',
    ],
    group: 'svara',
    groupLabelSan: 'स्वराः (ह्रस्व)',
    groupLabelEn: 'Vocalic R',
  },
  ॠ: {
    letter: 'ॠ',
    iast: 'ṝ',
    wordSan: 'ॠकारः',
    wordIast: 'ṝkāraḥ',
    wordEn: 'Long Vocalic R',
    wordHi: 'दीर्घ ॠ',
    emoji: '✨',
    strokeOrder: [
      '1. Draw letter "ऋ"',
      '2. Add double lower loop on the right side',
      '3. Cap with top bar',
    ],
    group: 'svara',
    groupLabelSan: 'स्वराः (दीर्घ)',
    groupLabelEn: 'Long Vocalic R',
  },
  ऌ: {
    letter: 'ऌ',
    iast: 'ḷ',
    wordSan: 'ऌकारः',
    wordIast: 'ḷkāraḥ',
    wordEn: 'Vocalic L',
    wordHi: 'ऌ स्वर',
    emoji: '📜',
    strokeOrder: [
      '1. Left downward spiral curve',
      '2. Right upward hook',
      '3. Cap with top bar',
    ],
    group: 'svara',
    groupLabelSan: 'स्वराः (ह्रस्व)',
    groupLabelEn: 'Vocalic L',
  },
  ए: {
    letter: 'ए',
    iast: 'e',
    wordSan: 'एणः',
    wordIast: 'eṇaḥ',
    wordEn: 'Spotted Deer',
    wordHi: 'हिरण',
    emoji: '🦌',
    strokeOrder: [
      '1. Left stem angling down-right',
      '2. Right straight vertical stem with inner hook',
      '3. Cap with top horizontal bar',
    ],
    group: 'svara',
    groupLabelSan: 'स्वराः (संयुक्त)',
    groupLabelEn: 'Diphthong Vowel',
  },
  ऐ: {
    letter: 'ऐ',
    iast: 'ai',
    wordSan: 'ऐरावतः',
    wordIast: 'airāvataḥ',
    wordEn: 'Royal White Elephant',
    wordHi: 'ऐरावत हाथी',
    emoji: '🐘',
    strokeOrder: [
      '1. Draw base letter "ए"',
      '2. Add slanted top antenna (मात्रा) pointing top-left',
      '3. Cap with top horizontal bar',
    ],
    group: 'svara',
    groupLabelSan: 'स्वराः (संयुक्त)',
    groupLabelEn: 'Diphthong Vowel',
  },
  ओ: {
    letter: 'ओ',
    iast: 'o',
    wordSan: 'ओष्ठः',
    wordIast: 'oṣṭhaḥ',
    wordEn: 'Lips',
    wordHi: 'होंठ',
    emoji: '👄',
    strokeOrder: [
      '1. Draw body "आ"',
      '2. Add slanted top accent stroke on the second stem',
      '3. Cap with top bar',
    ],
    group: 'svara',
    groupLabelSan: 'स्वराः (संयुक्त)',
    groupLabelEn: 'Diphthong Vowel',
  },
  औ: {
    letter: 'औ',
    iast: 'au',
    wordSan: 'औषधम्',
    wordIast: 'auṣadham',
    wordEn: 'Medicine / Herb',
    wordHi: 'दवा / औषधि',
    emoji: '🌿',
    strokeOrder: [
      '1. Draw body "आ"',
      '2. Add two slanted top accent strokes above second stem',
      '3. Cap with top bar',
    ],
    group: 'svara',
    groupLabelSan: 'स्वराः (संयुक्त)',
    groupLabelEn: 'Diphthong Vowel',
  },
  अं: {
    letter: 'अं',
    iast: 'aṁ',
    wordSan: 'अङ्कुरः',
    wordIast: 'aṅkuraḥ',
    wordEn: 'Sprout / Seedling',
    wordHi: 'अंकुर',
    emoji: '🌱',
    strokeOrder: [
      '1. Draw letter "अ"',
      '2. Cap with top bar',
      '3. Place dot (अनुस्वारः) centered above top bar',
    ],
    group: 'ayogavaha',
    groupLabelSan: 'अयोगवाहः (अनुस्वारः)',
    groupLabelEn: 'Nasal Dot (Anusvāra)',
  },
  अः: {
    letter: 'अः',
    iast: 'aḥ',
    wordSan: 'अन्तः',
    wordIast: 'antaḥ',
    wordEn: 'Within / Inner',
    wordHi: 'भीतर / विसर्ग',
    emoji: '🕊️',
    strokeOrder: [
      '1. Draw letter "अ"',
      '2. Cap with top bar',
      '3. Place two vertical dots (विसर्गः) to the right',
    ],
    group: 'ayogavaha',
    groupLabelSan: 'अयोगवाहः (विसर्गः)',
    groupLabelEn: 'Aspirate Dots (Visarga)',
  },


  अँ: {
    letter: 'अँ',
    iast: 'ã',
    wordSan: 'अँगनम्',
    wordIast: 'aṅganam',
    wordEn: 'Courtyard',
    wordHi: 'आँगन',
    hoverNote: 'Teaching note: अँ rarely begins Sanskrit words, so अँगनम् is a friendly sound example—not a classical word starting with अँ.',
    emoji: '🏡',
    strokeOrder: [
      '1. Draw letter "अ"',
      '2. Cap with top bar',
      '3. Place candrabindu (ँ) — crescent with a dot — centered above the bar',
    ],
    group: 'ayogavaha',
    groupLabelSan: 'अयोगवाहः (चन्द्रबिन्दुः)',
    groupLabelEn: 'Candrabindu (Nasal)',
  },
  // ==========================================
  // KA-VARGA (Velar / कण्ठ्य)
  // ==========================================
  क: {
    letter: 'क',
    iast: 'ka',
    wordSan: 'कमलम्',
    wordIast: 'kamalam',
    wordEn: 'Lotus Flower',
    wordHi: 'कमल',
    emoji: '🪷',
    strokeOrder: [
      '1. Draw vertical backbone stem',
      '2. Draw closed oval loop on left',
      '3. Draw open curve on right',
      '4. Cap with top bar (शिरोरेखा)',
    ],
    group: 'sparsha',
    groupLabelSan: 'क-वर्गः (कण्ठ्य)',
    groupLabelEn: 'Velar / Throat',
  },
  ख: {
    letter: 'ख',
    iast: 'kha',
    wordSan: 'खगः',
    wordIast: 'khagaḥ',
    wordEn: 'Flying Bird',
    wordHi: 'पक्षी',
    emoji: '🐦',
    strokeOrder: [
      '1. Upper curved loop down to base',
      '2. Curve up to meet vertical right stem',
      '3. Draw inner circular loop on vertical stem',
      '4. Cap with top bar',
    ],
    group: 'sparsha',
    groupLabelSan: 'क-वर्गः (कण्ठ्य)',
    groupLabelEn: 'Velar / Throat',
  },
  ग: {
    letter: 'ग',
    iast: 'ga',
    wordSan: 'गजः',
    wordIast: 'gajaḥ',
    wordEn: 'Elephant',
    wordHi: 'हाथी',
    emoji: '🐘',
    strokeOrder: [
      '1. Left vertical stroke looping inward to left',
      '2. Right full straight vertical stem',
      '3. Cap with top bar across both',
    ],
    group: 'sparsha',
    groupLabelSan: 'क-वर्गः (कण्ठ्य)',
    groupLabelEn: 'Velar / Throat',
  },
  घ: {
    letter: 'घ',
    iast: 'gha',
    wordSan: 'घटः',
    wordIast: 'ghaṭaḥ',
    wordEn: 'Clay Water Pot',
    wordHi: 'घड़ा',
    emoji: '🏺',
    strokeOrder: [
      '1. Upper left curve',
      '2. Lower left curve joining right stem',
      '3. Full vertical stem downward',
      '4. Cap with top bar',
    ],
    group: 'sparsha',
    groupLabelSan: 'क-वर्गः (कण्ठ्य)',
    groupLabelEn: 'Velar / Throat',
  },
  ङ: {
    letter: 'ङ',
    iast: 'ṅa',
    wordSan: 'वाङ्मयम्',
    wordIast: 'vāṅmayam',
    wordEn: 'Literature / Speech',
    wordHi: 'वाङ्मय',
    emoji: '📜',
    strokeOrder: [
      '1. Short vertical peg',
      '2. S-curve moving right then left',
      '3. Small dot to the right of the middle belly',
      '4. Cap with top bar',
    ],
    group: 'sparsha',
    groupLabelSan: 'क-वर्गः (नासिक्य)',
    groupLabelEn: 'Velar Nasal',
  },

  // ==========================================
  // CHA-VARGA (Palatal / तालव्य)
  // ==========================================
  च: {
    letter: 'च',
    iast: 'ca',
    wordSan: 'चक्रम्',
    wordIast: 'cakram',
    wordEn: 'Wheel / Disc',
    wordHi: 'पहिया / चक्र',
    emoji: '☸️',
    strokeOrder: [
      '1. Small horizontal start dash',
      '2. Dropping cup curve swinging up to right stem',
      '3. Vertical standing stem',
      '4. Cap with top bar',
    ],
    group: 'sparsha',
    groupLabelSan: 'च-वर्गः (तालव्य)',
    groupLabelEn: 'Palatal',
  },
  छ: {
    letter: 'छ',
    iast: 'cha',
    wordSan: 'छत्रम्',
    wordIast: 'chatram',
    wordEn: 'Umbrella',
    wordHi: 'छाता',
    emoji: '☂️',
    strokeOrder: [
      '1. Upper left loop',
      '2. Lower loop rising into a small closed eyelet loop',
      '3. Tiny vertical connection to top roof bar',
      '4. Cap with top bar',
    ],
    group: 'sparsha',
    groupLabelSan: 'च-वर्गः (तालव्य)',
    groupLabelEn: 'Palatal',
  },
  ज: {
    letter: 'ज',
    iast: 'ja',
    wordSan: 'जम्बूः',
    wordIast: 'jambūḥ',
    wordEn: 'Rose Apple / Berry',
    wordHi: 'जामुन',
    emoji: '🫐',
    strokeOrder: [
      '1. Left U-curve starting from middle connector',
      '2. Middle horizontal link joining vertical stem',
      '3. Vertical stem downward',
      '4. Cap with top bar',
    ],
    group: 'sparsha',
    groupLabelSan: 'च-वर्गः (तालव्य)',
    groupLabelEn: 'Palatal',
  },
  झ: {
    letter: 'झ',
    iast: 'jha',
    wordSan: 'झषः',
    wordIast: 'jhaṣaḥ',
    wordEn: 'Fish',
    wordHi: 'मछली',
    emoji: '🐟',
    strokeOrder: [
      '1. Draw shape like "इ"',
      '2. Horizontal bridge from belly to right stem',
      '3. Vertical standing stem',
      '4. Cap with top bar',
    ],
    group: 'sparsha',
    groupLabelSan: 'च-वर्गः (तालव्य)',
    groupLabelEn: 'Palatal',
  },
  ञ: {
    letter: 'ञ',
    iast: 'ña',
    wordSan: 'चञ्चुः',
    wordIast: 'cañcuḥ',
    wordEn: 'Bird Beak',
    wordHi: 'चोंच',
    emoji: '🦜',
    strokeOrder: [
      '1. C-shaped curve open to right',
      '2. Middle horizontal link to vertical stem',
      '3. Vertical standing stem',
      '4. Cap with top bar',
    ],
    group: 'sparsha',
    groupLabelSan: 'च-वर्गः (नासिक्य)',
    groupLabelEn: 'Palatal Nasal',
  },

  // ==========================================
  // TA-VARGA (Retroflex / मूर्धन्य)
  // ==========================================
  ट: {
    letter: 'ट',
    iast: 'ṭa',
    wordSan: 'टङ्कः',
    wordIast: 'ṭaṅkaḥ',
    wordEn: 'Axe / Chisel',
    wordHi: 'कुल्हाड़ी / छेनी',
    emoji: '🪓',
    strokeOrder: [
      '1. Short vertical center peg down',
      '2. Large rounded C-curve open to left',
      '3. Cap with top bar',
    ],
    group: 'sparsha',
    groupLabelSan: 'ट-वर्गः (मूर्धन्य)',
    groupLabelEn: 'Retroflex',
  },
  ठ: {
    letter: 'ठ',
    iast: 'ṭha',
    wordSan: 'ठक्कुरः',
    wordIast: 'ṭhakkuraḥ',
    wordEn: 'Deity / Idol',
    wordHi: 'ठाकुरजी',
    emoji: '🪔',
    strokeOrder: [
      '1. Short vertical peg down',
      '2. Complete circular loop',
      '3. Cap with top bar',
    ],
    group: 'sparsha',
    groupLabelSan: 'ट-वर्गः (मूर्धन्य)',
    groupLabelEn: 'Retroflex',
  },
  ड: {
    letter: 'ड',
    iast: 'ḍa',
    wordSan: 'डमरुः',
    wordIast: 'ḍamaruḥ',
    wordEn: 'Shiva Drum',
    wordHi: 'डमरू',
    emoji: '🪘',
    strokeOrder: [
      '1. Short vertical peg down',
      '2. S-curve sweeping right then left',
      '3. Cap with top bar',
    ],
    group: 'sparsha',
    groupLabelSan: 'ट-वर्गः (मूर्धन्य)',
    groupLabelEn: 'Retroflex',
  },
  ढ: {
    letter: 'ढ',
    iast: 'ḍha',
    wordSan: 'ढक्का',
    wordIast: 'ḍhakkā',
    wordEn: 'Kettle Drum',
    wordHi: 'ढोल',
    emoji: '🥁',
    strokeOrder: [
      '1. Short vertical peg down',
      '2. Big C-curve curling inside into a loop at bottom right',
      '3. Cap with top bar',
    ],
    group: 'sparsha',
    groupLabelSan: 'ट-वर्गः (मूर्धन्य)',
    groupLabelEn: 'Retroflex',
  },
  ण: {
    letter: 'ण',
    iast: 'ṇa',
    wordSan: 'बाणः',
    wordIast: 'bāṇaḥ',
    wordEn: 'Arrow',
    wordHi: 'तीर / बाण',
    emoji: '🏹',
    strokeOrder: [
      '1. Left U-shaped loop dipping down and returning up',
      '2. Right full straight vertical stem',
      '3. Cap with top bar across both',
    ],
    group: 'sparsha',
    groupLabelSan: 'ट-वर्गः (नासिक्य)',
    groupLabelEn: 'Retroflex Nasal',
  },

  // ==========================================
  // THA-VARGA (Dental / दन्त्य)
  // ==========================================
  त: {
    letter: 'त',
    iast: 'ta',
    wordSan: 'तरुः',
    wordIast: 'taruḥ',
    wordEn: 'Tree',
    wordHi: 'पेड़',
    emoji: '🌳',
    strokeOrder: [
      '1. Vertical stem downward',
      '2. Horizontal stroke from mid-stem curving downward to left',
      '3. Cap with top bar',
    ],
    group: 'sparsha',
    groupLabelSan: 'त-वर्गः (दन्त्य)',
    groupLabelEn: 'Dental',
  },
  थ: {
    letter: 'थ',
    iast: 'tha',
    wordSan: 'थूत्कारः',
    wordIast: 'thūtkāraḥ',
    wordEn: 'Trumpeting',
    wordHi: 'चिंघाड़',
    emoji: '🎺',
    strokeOrder: [
      '1. Upper left spiral knot loop',
      '2. Lower body curve meeting vertical stem',
      '3. Vertical stem downward',
      '4. Cap with top bar (leaving the knot open)',
    ],
    group: 'sparsha',
    groupLabelSan: 'त-वर्गः (दन्त्य)',
    groupLabelEn: 'Dental',
  },
  द: {
    letter: 'द',
    iast: 'da',
    wordSan: 'दन्तः',
    wordIast: 'dantaḥ',
    wordEn: 'Tooth',
    wordHi: 'दाँत',
    emoji: '🦷',
    strokeOrder: [
      '1. Short vertical peg',
      '2. C-curve moving right',
      '3. Diagonal tail flicking downward-right',
      '4. Cap with top bar',
    ],
    group: 'sparsha',
    groupLabelSan: 'त-वर्गः (दन्त्य)',
    groupLabelEn: 'Dental',
  },
  ध: {
    letter: 'ध',
    iast: 'dha',
    wordSan: 'धनुः',
    wordIast: 'dhanuḥ',
    wordEn: 'Bow',
    wordHi: 'धनुष',
    emoji: '🏹',
    strokeOrder: [
      '1. Upper left spiral eyelet',
      '2. Two curved bellies meeting right vertical stem',
      '3. Vertical stem downward',
      '4. Cap with top bar (leaving the eyelet open)',
    ],
    group: 'sparsha',
    groupLabelSan: 'त-वर्गः (दन्त्य)',
    groupLabelEn: 'Dental',
  },
  न: {
    letter: 'न',
    iast: 'na',
    wordSan: 'नौका',
    wordIast: 'naukā',
    wordEn: 'Boat / Ship',
    wordHi: 'नाव',
    emoji: '⛵',
    strokeOrder: [
      '1. Small triangular/circular knot on left',
      '2. Horizontal bridge to vertical stem',
      '3. Vertical stem downward',
      '4. Cap with top bar',
    ],
    group: 'sparsha',
    groupLabelSan: 'त-वर्गः (नासिक्य)',
    groupLabelEn: 'Dental Nasal',
  },

  // ==========================================
  // PA-VARGA (Labial / ओष्ठ्य)
  // ==========================================
  प: {
    letter: 'प',
    iast: 'pa',
    wordSan: 'पत्रम्',
    wordIast: 'patram',
    wordEn: 'Leaf',
    wordHi: 'पत्ता',
    emoji: '🍃',
    strokeOrder: [
      '1. Left downward stroke curving right',
      '2. Full straight vertical stem meeting the curve',
      '3. Cap with top bar',
    ],
    group: 'sparsha',
    groupLabelSan: 'प-वर्गः (ओष्ठ्य)',
    groupLabelEn: 'Labial / Lips',
  },
  फ: {
    letter: 'फ',
    iast: 'pha',
    wordSan: 'फलम्',
    wordIast: 'phalam',
    wordEn: 'Fruit',
    wordHi: 'फल',
    emoji: '🍎',
    strokeOrder: [
      '1. Draw shape of "प"',
      '2. Hook right side downward like an inverted tail',
      '3. Cap with top bar',
    ],
    group: 'sparsha',
    groupLabelSan: 'प-वर्गः (ओष्ठ्य)',
    groupLabelEn: 'Labial / Lips',
  },
  ब: {
    letter: 'ब',
    iast: 'ba',
    wordSan: 'बकः',
    wordIast: 'bakaḥ',
    wordEn: 'Crane / Heron',
    wordHi: 'बगुला',
    emoji: '🪿',
    strokeOrder: [
      '1. Vertical stem downward',
      '2. Closed circular belly on left side of stem',
      '3. Diagonal slash slicing inside the belly',
      '4. Cap with top bar',
    ],
    group: 'sparsha',
    groupLabelSan: 'प-वर्गः (ओष्ठ्य)',
    groupLabelEn: 'Labial / Lips',
  },
  भ: {
    letter: 'भ',
    iast: 'bha',
    wordSan: 'भल्लुकः',
    wordIast: 'bhallukaḥ',
    wordEn: 'Bear',
    wordHi: 'भालू',
    emoji: '🐻',
    strokeOrder: [
      '1. Top-left eyelet spiral down',
      '2. Lower loop bridging across to right stem',
      '3. Vertical stem downward',
      '4. Cap with top bar (leaving top loop open)',
    ],
    group: 'sparsha',
    groupLabelSan: 'प-वर्गः (ओष्ठ्य)',
    groupLabelEn: 'Labial / Lips',
  },
  म: {
    letter: 'म',
    iast: 'ma',
    wordSan: 'मयूरः',
    wordIast: 'mayūraḥ',
    wordEn: 'Peacock',
    wordHi: 'मोर',
    emoji: '🦚',
    strokeOrder: [
      '1. Left downward stem looping inside at bottom',
      '2. Horizontal bridge to right stem',
      '3. Vertical stem downward',
      '4. Cap with top bar across entire letter',
    ],
    group: 'sparsha',
    groupLabelSan: 'प-वर्गः (नासिक्य)',
    groupLabelEn: 'Labial Nasal',
  },

  // ==========================================
  // ANTASTHA (Semi-vowels / अन्तःस्थाः)
  // ==========================================
  य: {
    letter: 'य',
    iast: 'ya',
    wordSan: 'यज्ञः',
    wordIast: 'yajñaḥ',
    wordEn: 'Sacred Flame / Yajna',
    wordHi: 'यज्ञ',
    emoji: '🔥',
    strokeOrder: [
      '1. Top left rounded curve',
      '2. Lower body swooping up to meet vertical stem',
      '3. Vertical stem downward',
      '4. Cap with top bar',
    ],
    group: 'antastha',
    groupLabelSan: 'अन्तःस्थाः (तालव्य)',
    groupLabelEn: 'Semi-vowel',
  },
  र: {
    letter: 'र',
    iast: 'ra',
    wordSan: 'रथः',
    wordIast: 'rathaḥ',
    wordEn: 'Chariot',
    wordHi: 'रथ',
    emoji: '🏎️',
    strokeOrder: [
      '1. Upper curved arc',
      '2. Diagonal leg extending down to the left',
      '3. Cap with top bar',
    ],
    group: 'antastha',
    groupLabelSan: 'अन्तःस्थाः (मूर्धन्य)',
    groupLabelEn: 'Semi-vowel',
  },
  ल: {
    letter: 'ल',
    iast: 'la',
    wordSan: 'लता',
    wordIast: 'latā',
    wordEn: 'Creeper Vine',
    wordHi: 'बेल / लता',
    emoji: '🌿',
    strokeOrder: [
      '1. Lower curved hook',
      '2. Upper arching loop connecting to vertical stem',
      '3. Vertical stem downward',
      '4. Cap with top bar',
    ],
    group: 'antastha',
    groupLabelSan: 'अन्तःस्थाः (दन्त्य)',
    groupLabelEn: 'Semi-vowel',
  },
  व: {
    letter: 'व',
    iast: 'va',
    wordSan: 'वटः',
    wordIast: 'vaṭaḥ',
    wordEn: 'Banyan Tree',
    wordHi: 'बरगद का पेड़',
    emoji: '🌳',
    strokeOrder: [
      '1. Vertical stem downward',
      '2. Rounded closed belly on the left',
      '3. Cap with top bar',
    ],
    group: 'antastha',
    groupLabelSan: 'अन्तःस्थाः (दन्तोष्ठ्य)',
    groupLabelEn: 'Semi-vowel',
  },

  // ==========================================
  // USHMANA (Sibilants & Aspirate / ऊष्माणः)
  // ==========================================
  श: {
    letter: 'श',
    iast: 'śa',
    wordSan: 'शुकः',
    wordIast: 'śukaḥ',
    wordEn: 'Parrot',
    wordHi: 'तोता',
    emoji: '🦜',
    strokeOrder: [
      '1. Upper left spiral loop',
      '2. Leg sloping down-left with lower eyelet',
      '3. Vertical standing stem on right',
      '4. Cap with top bar',
    ],
    group: 'ushmana',
    groupLabelSan: 'ऊष्माणः (तालव्य)',
    groupLabelEn: 'Palatal Sibilant',
  },
  ष: {
    letter: 'ष',
    iast: 'ṣa',
    wordSan: 'षण्मुखः',
    wordIast: 'ṣaṇmukhaḥ',
    wordEn: 'Six-faced Murugan',
    wordHi: 'षडानन (कार्तिकेय)',
    emoji: '🪶',
    strokeOrder: [
      '1. Draw shape of "प"',
      '2. Diagonal slash slicing through the interior',
      '3. Cap with top bar',
    ],
    group: 'ushmana',
    groupLabelSan: 'ऊष्माणः (मूर्धन्य)',
    groupLabelEn: 'Retroflex Sibilant',
  },
  स: {
    letter: 'स',
    iast: 'sa',
    wordSan: 'सिंहः',
    wordIast: 'siṁhaḥ',
    wordEn: 'Lion',
    wordHi: 'शेर',
    emoji: '🦁',
    strokeOrder: [
      '1. Draw shape of "र"',
      '2. Horizontal bridge connecting to vertical stem',
      '3. Vertical stem downward',
      '4. Cap with top bar across entire letter',
    ],
    group: 'ushmana',
    groupLabelSan: 'ऊष्माणः (दन्त्य)',
    groupLabelEn: 'Dental Sibilant',
  },
  ह: {
    letter: 'ह',
    iast: 'ha',
    wordSan: 'हंसः',
    wordIast: 'haṁsaḥ',
    wordEn: 'Swan',
    wordHi: 'हंस',
    emoji: '🦢',
    strokeOrder: [
      '1. Short vertical peg',
      '2. Upper curved chest bending right',
      '3. Lower tail curving gracefully down to left',
      '4. Cap with top bar',
    ],
    group: 'ushmana',
    groupLabelSan: 'ऊष्माणः (कण्ठ्य)',
    groupLabelEn: 'Aspirate / Throat',
  },

  // ==========================================
  // SAMYUKTA (Common Conjuncts / संयुक्तवत्)
  // ==========================================
  क्ष: {
    letter: 'क्ष',
    iast: 'kṣa',
    wordSan: 'क्षत्रियः',
    wordIast: 'kṣatriyaḥ',
    wordEn: 'Warrior',
    wordHi: 'क्षत्रिय / योद्धा',
    emoji: '⚔️',
    strokeOrder: [
      '1. Draw क-body with vertical stem',
      '2. Add ष-loop joined under/through the stem (क् + ष)',
      '3. Cap with top horizontal bar (शिरोरेखा)',
    ],
    group: 'samyukta',
    groupLabelSan: 'संयुक्तवत्',
    groupLabelEn: 'Conjunct',
  },
  ज्ञ: {
    letter: 'ज्ञ',
    iast: 'jña',
    wordSan: 'ज्ञानम्',
    wordIast: 'jñānam',
    wordEn: 'Knowledge',
    wordHi: 'ज्ञान',
    emoji: '📖',
    strokeOrder: [
      '1. Draw ज-body',
      '2. Join ञ-form to complete ज्ञ (ज् + ञ)',
      '3. Cap with top horizontal bar',
    ],
    group: 'samyukta',
    groupLabelSan: 'संयुक्तवत्',
    groupLabelEn: 'Conjunct',
  },
  त्र: {
    letter: 'त्र',
    iast: 'tra',
    wordSan: 'त्रिशूलम्',
    wordIast: 'triśūlam',
    wordEn: 'Trident',
    wordHi: 'त्रिशूल',
    emoji: '🔱',
    strokeOrder: [
      '1. Draw त stem and left curve',
      '2. Add र-curve (्र) tucked under/through',
      '3. Cap with top horizontal bar',
    ],
    group: 'samyukta',
    groupLabelSan: 'संयुक्तवत्',
    groupLabelEn: 'Conjunct',
  },
  श्र: {
    letter: 'श्र',
    iast: 'śra',
    wordSan: 'श्रीफलम्',
    wordIast: 'śrīphalam',
    wordEn: 'Coconut',
    wordHi: 'नारियल / श्रीफल',
    emoji: '🥥',
    strokeOrder: [
      '1. Draw श body with left loops',
      '2. Add र-curve (्र) joined to the stem',
      '3. Cap with top horizontal bar',
    ],
    group: 'samyukta',
    groupLabelSan: 'संयुक्तवत्',
    groupLabelEn: 'Conjunct',
  },
};

export const ALL_VARNAMALA_LETTERS = Object.values(VARNAMALA_MNEMONICS);

export function getLetterMnemonic(letter: string): LetterMnemonic | undefined {
  // Normalize candrabindu or spaces
  const clean = letter.trim();
  return VARNAMALA_MNEMONICS[clean];
}
